import React, { useEffect, useRef, useState } from 'react';
import './CrtEffect.scss';

export interface CrtEffectProps {
  /** Master switch to enable or disable the CRT effect. Default: true */
  enabled?: boolean;
  /** Intensity level of scanlines, flicker & vignette. Default: 'medium' */
  intensity?: 'subtle' | 'medium' | 'strong';
  /** Enable horizontal scanlines raster grid. Default: true */
  scanlines?: boolean;
  /** Enable phosphor screen flicker animation. Default: true */
  flicker?: boolean;
  /** Enable vignette & rounded bezel shadow. Default: true */
  vignette?: boolean;
  /** Enable rolling TV interference bar & glitch line. Default: true */
  interferenceBar?: boolean;
  /** Enable dynamic analog static noise overlay. Default: true */
  noise?: boolean;
  /** Show interactive floating toggle button in bottom-right corner. Default: true */
  showToggleButton?: boolean;
  /** LocalStorage key to save user preference. Set to null to disable persistence. Default: 'crt_effect_enabled' */
  persistKey?: string | null;
  /** Callback fired when CRT effect is toggled ON/OFF */
  onToggle?: (active: boolean) => void;
  /** Additional CSS class name */
  className?: string;
}

export const CrtEffect: React.FC<CrtEffectProps> = ({
  enabled = true,
  intensity = 'subtle',
  scanlines = true,
  flicker = true,
  vignette = true,
  interferenceBar = true,
  noise = true,
  showToggleButton = true,
  persistKey = 'crt_effect_enabled',
  onToggle,
  className = '',
}) => {
  const [active, setActive] = useState<boolean>(() => {
    if (persistKey && typeof window !== 'undefined') {
      const saved = localStorage.getItem(persistKey);
      if (saved !== null) {
        return saved === 'true';
      }
    }
    return enabled;
  });

  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Sync state if enabled prop changes from outside
  useEffect(() => {
    if (persistKey && typeof window !== 'undefined') {
      const saved = localStorage.getItem(persistKey);
      if (saved === null) {
        setActive(enabled);
      }
    } else {
      setActive(enabled);
    }
  }, [enabled, persistKey]);

  const toggleEffect = () => {
    const nextState = !active;
    setActive(nextState);
    if (persistKey && typeof window !== 'undefined') {
      localStorage.setItem(persistKey, String(nextState));
    }
    if (onToggle) {
      onToggle(nextState);
    }
  };

  // Dynamic noise canvas generator
  useEffect(() => {
    if (!active || !noise) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animId: number;
    let width = (canvas.width = 256);
    let height = (canvas.height = 256);

    const imgData = ctx.createImageData(width, height);
    const buffer32 = new Uint32Array(imgData.data.buffer);

    let lastTime = 0;
    const fpsInterval = 1000 / 24; // 24 FPS static noise to save performance

    const renderNoise = (time: number) => {
      animId = requestAnimationFrame(renderNoise);

      const delta = time - lastTime;
      if (delta < fpsInterval) return;
      lastTime = time - (delta % fpsInterval);

      const len = buffer32.length;
      for (let i = 0; i < len; i++) {
        if (Math.random() < 0.12) {
          const val = Math.floor(Math.random() * 255);
          // ABGR order for Little Endian: Alpha(0xFF) | Blue | Green | Red
          buffer32[i] = (0xff << 24) | (val << 16) | (val << 8) | val;
        } else {
          buffer32[i] = 0x00000000;
        }
      }

      ctx.putImageData(imgData, 0, 0);
    };

    animId = requestAnimationFrame(renderNoise);

    return () => {
      cancelAnimationFrame(animId);
    };
  }, [active, noise]);

  return (
    <>
      {active && (
        <div
          className={`crt-container intensity-${intensity} ${className}`}
          aria-hidden="true"
        >
          {scanlines && <div className="crt-scanlines" />}
          {flicker && <div className="crt-flicker" />}
          {vignette && <div className="crt-vignette" />}
          {interferenceBar && (
            <>
              <div className="crt-interference-bar" />
              <div className="crt-glitch-line" />
            </>
          )}
          {noise && <canvas ref={canvasRef} className="crt-noise-canvas" />}
          <div className="crt-glass-glare" />
        </div>
      )}

      {showToggleButton && (
        <button
          className={`crt-toggle-button ${active ? 'active' : ''}`}
          onClick={toggleEffect}
          title={active ? 'Disable CRT Effect' : 'Enable CRT Effect'}
          type="button"
        >
          <span className="crt-status-dot" />
          <span>CRT {active ? 'ON' : 'OFF'}</span>
        </button>
      )}
    </>
  );
};

export default CrtEffect;
